const POLL_MS = 2_000;
/** Session timed out or was consumed — TV should mint a fresh QR without operator action. */
export class PairingSessionRefreshError extends Error {
    reason;
    constructor(reason, message) {
        super(message);
        this.name = 'PairingSessionRefreshError';
        this.reason = reason;
    }
}
function abortError() {
    const error = new Error('Aborted');
    error.name = 'AbortError';
    return error;
}
function sleep(ms, signal) {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(abortError());
            return;
        }
        const timer = setTimeout(resolve, ms);
        signal?.addEventListener('abort', () => {
            clearTimeout(timer);
            reject(abortError());
        }, { once: true });
    });
}
export async function createTvPairingSession(input) {
    const deviceCode = await input.credentials.getOrCreateDeviceCode();
    const created = await input.api.createPairingSession({
        deviceCode,
        appVersion: input.appVersion,
        platform: input.identity.platform,
        playerBuild: input.identity.playerBuild,
        capabilities: [...input.identity.capabilities],
        ...(input.identity.manufacturer
            ? { manufacturer: input.identity.manufacturer }
            : {}),
        ...(input.identity.model ? { model: input.identity.model } : {}),
    });
    return { ...created, deviceCode };
}
/**
 * Poll until the phone approves and the one-time token is delivered.
 * Expired / already-used throw PairingSessionRefreshError as a safety net —
 * the TV shell should also rotate on a timer from expiresAt (Netflix-style).
 */
export async function awaitTvPairingSession(input) {
    const code = input.code.trim().toUpperCase();
    while (true) {
        if (input.signal?.aborted) {
            throw abortError();
        }
        const poll = await input.api.pollPairingSession(code);
        if (poll.status === 'expired') {
            throw new PairingSessionRefreshError('expired', 'Pairing code expired. Getting a new code…');
        }
        if (poll.status === 'claimed' && !poll.token) {
            throw new PairingSessionRefreshError('already_used', 'This pairing code was already used. Getting a new code…');
        }
        if (poll.token && poll.deviceId) {
            await input.credentials.set({
                deviceId: poll.deviceId,
                token: poll.token,
                screenId: poll.screenId ?? null,
            });
            return { deviceId: poll.deviceId, screenId: poll.screenId ?? null };
        }
        await sleep(POLL_MS, input.signal);
    }
}
//# sourceMappingURL=session.js.map